import { DocumentAIAnalysis, Document } from "../types";

// Helper for REST API
// Helper for REST API
import { auth } from "./firebase";

// Helper for REST API via Backend Proxy
const generateContent = async (prompt: string, base64Image?: { data: string, mimeType: string }) => {

    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    // Get Auth Token
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("User must be logged in to use AI features.");
    const token = await currentUser.getIdToken();

    const body: any = {
        prompt,
        model: 'gemini-flash-latest'
    };

    if (base64Image) {
        body.base64Image = base64Image;
    }

    const response = await fetch(`${API_BASE_URL}/api/ai/generate`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        const err = await response.text();
        console.error("AI Proxy Error Details:", { status: response.status, statusText: response.statusText, body: err });
        throw new Error(`AI Service Error: ${err}`);
    }

    const data = await response.json();
    return data.text;
};

export const analyzeDocument = async (
    file: File,
    // apiKey param removed
    existingDocId: string,
    companyId: string
): Promise<DocumentAIAnalysis> => {

    // Convert file to base64
    const base64Data = await fileToGenerativePart(file);

    const prompt = `
    Analyze this document completely and strictly.
    Extract the following structured data in JSON format only.
    Do NOT interpret or guess values that are not visible.
    If a value is missing, use null.

    Required JSON Schema:
    {
        "doc_type": "invoice" | "receipt" | "bank_statement" | "contract" | "other",
        "doc_date": "YYYY-MM-DD" or null,
        "details": {
            "issuer": "string" or null,
            "recipient": "string" or null,
            "vat_number": "string" or null,
            "invoice_number": "string" or null
        },
        "amounts": {
            "net": number (float),
            "vat": number (float),
            "gross": number (float),
            "currency": "EUR" | "USD" | "GBP"
        },
        "summary": "1-2 sentence semantic summary of what this document is about.",
        "keywords": ["tag1", "tag2", "tag3"],
        "confidence_score": number (0.0 - 1.0 based on visibility),
        "installment_info": {
            "is_installment": boolean,
            "installment_date": "YYYY-MM-DD" or null (Date of the specific installment, e.g. from 'Ποσό δόσης της 30/01/2026'),
            "installment_amount": number (float) or null,
            "installment_number": number or null,
            "total_debt_amount": number (float) or null,
            "payment_code": string or null
        }
    }

    Special Instruction for Greek Documents:
    - Look for the phrase "Ποσό δόσης" (or "Ποσό Δόσης", case-insensitive) or "Ποσό δόσης της" followed by a date.
    - If found, set "is_installment" to true and extract the date and amount.
    - Look for "Συνολικό Ποσό Οφειλής", "Συνολικό ποσό οφειλής δήλωσης", or "ΠΛΗΡΩΤΕΟ ΠΟΣΟ" to extract "total_debt_amount".
    - Look for "Ταυτότητα Οφειλής", "Κωδικός Πληρωμής", or "BARCODE" (especially if it starts with "RF") to extract "payment_code" (remove spaces).
    - "doc_date" should be the issue date.
    - "installment_date" should be the specific due date, often labeled as "ΗΜ/ΝΙΑ ΕΜΠΡΟΘΕΣΜΗΣ ΠΛΗΡΩΜΗΣ" or "Προθεσμία πληρωμής".
    - **SPECIAL RULE FOR e-EFKA**: For "e-ΕΦΚΑ" documents, the due date is often located **BELOW** the label "ΗΜ/ΝΙΑ ΕΜΠΡΟΘΕΣΜΗΣ ΠΛΗΡΩΜΗΣ". Look at the line immediately following this header.
    `;

    try {
        const text = await generateContent(prompt, base64Data.inlineData);

        const jsonString = text.replace(/```json/g, '').replace(/```/g, '').trim();
        const data = JSON.parse(jsonString);

        // Enhance with metadata
        const analysis: DocumentAIAnalysis = {
            doc_id: existingDocId,
            company_id: companyId,
            doc_type: data.doc_type || 'unknown',
            doc_date: data.doc_date,
            confidence_score: data.confidence_score || 0.5,
            details: data.details,
            amounts: data.amounts,
            summary: data.summary,
            keywords: data.keywords || [],
            installment_info: data.installment_info,
            period: data.doc_date ? {
                month: new Date(data.doc_date).getMonth() + 1,
                year: new Date(data.doc_date).getFullYear()
            } : undefined,
            processed_at: new Date().toISOString(),
            model_used: "gemini-flash-latest"
        };

        return analysis;

    } catch (error) {
        console.error("AI Analysis Failed:", error);
        throw new Error("Failed to analyze document with AI.");
    }
};

// --- ROBUST AI ASSISTANT IMPLEMENTATION V3 (Direct Financial Dataset) ---

export interface AiChatResponse {
    answer: string;
    sources: Document[];
}

/**
 * Step 1: Prompt Building with Strict JSON Context
 */
const buildJsonContext = (documents: Document[]): string => {
    if (!documents || documents.length === 0) return "[]";

    // Sort by Date Descending for context
    const sorted = [...documents].sort((a, b) => {
        const da = a.ai_analysis?.doc_date || '';
        const db = b.ai_analysis?.doc_date || '';
        return db.localeCompare(da);
    });

    const simplifiedDocs = sorted.slice(0, 50).map(doc => { // Limit context size to 50 most recent
        const a = doc.ai_analysis;
        if (!a) return null;

        const info = a.installment_info;

        return {
            id: doc.id,
            category: doc.typeName || 'Unknown', // Matches 'Category' column in UI
            title: doc.title || 'Untitled',      // Matches 'Document' column in UI
            type: a.doc_type || 'document',
            issuer: a.details?.issuer || 'Unknown',
            date: a.doc_date,
            summary: a.summary,
            amount: `${info?.total_debt_amount || a.amounts?.gross || 0} ${a.amounts?.currency || 'EUR'}`,
            payment_due: info?.installment_date || "N/A",
            payment_amount: info?.installment_amount || info?.total_debt_amount || "N/A",
            payment_code: info?.payment_code || "N/A"
        };
    }).filter((d): d is NonNullable<typeof d> => d !== null);

    return JSON.stringify(simplifiedDocs, null, 2);
};

/**
 * Step 2: Generation with Retry
 */
const generateResponse = async (question: string, jsonContext: string, attempt = 1): Promise<string> => {
    const prompt = `
    Role: Financial Advisor.
    Input: JSON Dataset (Financial Overview).
    User Question: "${question}"
    
    Context (My Financial Documents):
    ${jsonContext}
    
    Instructions:
    1. Answer ONLY using the provided JSON dataset. This is the user's "Financial Overview".
    2. Pay close attention to the "category" and "title" fields. Users may refer to documents by their category name (e.g. "FMY", "VAT", "EFKA").
    3. If the user asks for totals (e.g. "total debt"), calculate strictly from the JSON values.
    4. If the answer is not in the JSON, say "I reviewed your financial overview but couldn't find that information."
    5. Cite sources as [doc_id] when referring to specific items.
    6. Reply in the User's Language.
    7. Be concise.
    `;

    try {
        const answer = await generateContent(prompt);
        if (!answer || answer.trim().length < 5) throw new Error("Empty response");
        return answer;
    } catch (e) {
        if (attempt < 2) {
            console.log("Retrying generation...");
            return generateResponse(question, jsonContext, attempt + 1);
        }
        throw e;
    }
};

// New Main Function: Takes Filtered Docs directly
export const askFinancialAdvisor = async (
    question: string,
    financialDocs: Document[]
    // apiKey param removed
): Promise<AiChatResponse> => {

    // 0. Quick Greeting check
    const lowerQ = question.toLowerCase();
    if (['hello', 'hi', 'γεια', 'καλημερα'].some(w => lowerQ.includes(w)) && lowerQ.length < 20) {
        return { answer: "Hello! I can help you analyze your financial overview.", sources: [] };
    }

    // 1. Check Data
    if (!financialDocs || financialDocs.length === 0) {
        return {
            answer: "I reviewed your financial overview but found no records.",
            sources: []
        };
    }

    // 2. Build Context directly from the passed dataset
    // Filter: Include only PENDING documents (exclude paid)
    const pendingDocs = financialDocs.filter(doc => doc.paymentStatus !== 'paid');

    if (pendingDocs.length === 0) {
        return {
            answer: "Great news! You have no pending financial obligations at the moment.",
            sources: []
        };
    }

    const jsonContext = buildJsonContext(pendingDocs);

    // 3. Generate Answer
    try {
        const answer = await generateResponse(question, jsonContext);

        // Extract Citations
        const citations = new Set<string>();
        const regex = /\[([a-zA-Z0-9_-]+)\]/g;
        let m;
        while ((m = regex.exec(answer)) !== null) citations.add(m[1]);

        const relevantDocs = financialDocs.filter(d => citations.has(d.id));

        // Fallback citations: If AI mentioned specifics but didn't tag, try to find by ID if possible, or just return top 3 if it was a general summary query
        const finalSources = relevantDocs.length > 0 ? relevantDocs : [];
        const cleanAnswer = answer.replace(/\[([a-zA-Z0-9_-]+)\]/g, '').trim();

        return { answer: cleanAnswer, sources: finalSources };

    } catch (err) {
        console.error("Advisor Error:", err);
        return { answer: "Service temporarily unavailable.", sources: [] };
    }
};

async function fileToGenerativePart(file: File): Promise<{ inlineData: { data: string; mimeType: string } }> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64String = (reader.result as string).split(',')[1];
            resolve({
                inlineData: {
                    data: base64String,
                    mimeType: file.type
                }
            });
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}
